import type { LanguageTarget, PlatformTarget } from './types'

/**
 * Campaign language/platform targeting: the one code-to-label mapping shared by
 * the create and edit forms, the detail view, the campaigns list and the history
 * block (phase 4 D-03), and the creative language gap rule (phase 4 D-10).
 *
 * Spec docs/spec-ad-language-platform-targeting.md: D2 (values, All/Both
 * defaults), D12 (mismatch warns, never blocks), D14 (labels, keys only under
 * campaigns.*).
 *
 * Pure: no Supabase, no React, no i18n instance.
 */

export type TargetingField = 'language_target' | 'platform_target'

export const LANGUAGE_TARGET_OPTIONS: { value: LanguageTarget; labelKey: string }[] = [
  { value: 'all', labelKey: 'campaigns.language_target_all' },
  { value: 'pt', labelKey: 'campaigns.language_target_pt' },
  { value: 'en', labelKey: 'campaigns.language_target_en' }
]

export const PLATFORM_TARGET_OPTIONS: { value: PlatformTarget; labelKey: string }[] = [
  { value: 'all', labelKey: 'campaigns.platform_target_all' },
  { value: 'mobile', labelKey: 'campaigns.platform_target_mobile' },
  { value: 'web', labelKey: 'campaigns.platform_target_web' }
]

export const TARGETING_FIELD_LABEL_KEYS: Record<TargetingField, string> = {
  language_target: 'campaigns.language_target',
  platform_target: 'campaigns.platform_target'
}

const OPTIONS_BY_FIELD: Record<TargetingField, { value: string; labelKey: string }[]> = {
  language_target: LANGUAGE_TARGET_OPTIONS,
  platform_target: PLATFORM_TARGET_OPTIONS
}

export const isTargetingField = (field: unknown): field is TargetingField =>
  field === 'language_target' || field === 'platform_target'

export const targetingLabelKey = (field: TargetingField, code: string | null | undefined): string | null => {
  if (code === null || code === undefined) return null
  const option = OPTIONS_BY_FIELD[field].find((o) => o.value === code)
  return option ? option.labelKey : null
}

// D-03: an unknown code (or an empty translation) shows the raw value.
export const targetingLabel = (
  field: TargetingField,
  code: string | null | undefined,
  t: (key: string) => string
): string => {
  const raw = code ?? ''
  const key = targetingLabelKey(field, code)
  if (!key) return raw
  return t(key) || raw
}

export type CreativeGapField = 'title' | 'description'

export type CreativeLanguageGap = {
  creativeId: string
  title: string | null
  missing: CreativeGapField[]
}

export type CreativeGapInput = {
  language_target?: string | null
  campaign_creatives?: Array<{
    creatives?: {
      id: string
      title?: string | null
      description?: string | null
      title_pt?: string | null
      description_pt?: string | null
    } | null
  }> | null
}

const isEmpty = (value: string | null | undefined) => !value?.trim()

// D-10: 'pt' checks title_pt/description_pt, 'en' checks title/description,
// 'all' (or anything else) never warns. Whitespace-only counts as empty.
export const getCreativeLanguageGaps = (campaign: CreativeGapInput): CreativeLanguageGap[] => {
  const target = campaign.language_target
  if (target !== 'pt' && target !== 'en') return []

  const gaps: CreativeLanguageGap[] = []
  ;(campaign.campaign_creatives ?? []).forEach((assignment) => {
    const creative = assignment?.creatives
    if (!creative) return

    const title = target === 'pt' ? creative.title_pt : creative.title
    const description = target === 'pt' ? creative.description_pt : creative.description
    const missing: CreativeGapField[] = []
    if (isEmpty(title)) missing.push('title')
    if (isEmpty(description)) missing.push('description')
    if (missing.length === 0) return

    gaps.push({
      creativeId: creative.id,
      title: creative.title?.trim() || creative.title_pt?.trim() || null,
      missing
    })
  })
  return gaps
}
